import type {
  GenerateCaptionsBody,
  GenerateCaptionsResponse,
} from "@nlc/shared-types";
import type { UseMutationResult } from "@tanstack/react-query";

import { MAX_CAPTION_IMAGES } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";

import type { DesignFile } from "../components/schedule-wizard/design-step/types";

import { captureDesignImage } from "../components/schedule-wizard/caption-generator/captureDesignImage";

const AI_API_URL = import.meta.env.VITE_MBC_MASTRA_API_URL ?? "/api/ai/v1";
const FALLBACK_ERROR = "We couldn't write captions this time. Try again.";

export type GenerateCaptionsInput = {
  designs: DesignFile[];
} & Omit<GenerateCaptionsBody, "images">;

async function generateCaptions({
  designs,
  ...body
}: GenerateCaptionsInput): Promise<GenerateCaptionsResponse> {
  const captured = await Promise.all(
    designs
      .slice(0, MAX_CAPTION_IMAGES)
      .map((design) => captureDesignImage(design)),
  );
  const images = captured.filter((image) => image !== null);

  const response = await fetch(`${AI_API_URL}/social-publishing/captions`, {
    body: JSON.stringify({ ...body, images }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as {
      error?: string;
    } | null;

    throw new Error(error?.error ?? FALLBACK_ERROR);
  }

  return response.json();
}

export function useGenerateCaptions(): UseMutationResult<
  GenerateCaptionsResponse,
  Error,
  GenerateCaptionsInput
> {
  return useMutation({ mutationFn: generateCaptions });
}
